import { useMemo, useState } from "react";
import { Truck, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// ---------- localized copy ---------- //
const COPY = {
  de: {
    badge: "HAUSEIGENER FUHRPARK",
    overline: "— Routen & Pläne",
    title_1: "Wir kommen",
    title_2: "zu Ihnen.",
    intro:
      "Ein wichtiger Faktor unserer Flexibilität ist die Unabhängigkeit von Fremdtransportern. Durch unseren hauseigenen Abhol- und Lieferservice sind wir noch schneller bei Ihnen.",
    days: {
      modo: { label: "Montag & Donnerstag", short: "Mo · Do" },
      mi:   { label: "Mittwoch",             short: "Mi" },
      difr: { label: "Dienstag & Freitag",   short: "Di · Fr" },
    },
    spedition_title: "Spedition & Fuhrpark",
    spedition_hint: "Für nähere Auskünfte steht Ihnen unsere Spedition jederzeit mit Rat und Tat zur Seite.",
    stops_title: "Halte-Regionen",
    cta_contact: "Route anfragen",
  },
  en: {
    badge: "IN-HOUSE FLEET",
    overline: "— Routes & schedule",
    title_1: "We come",
    title_2: "to you.",
    intro:
      "A key factor of our flexibility is our independence from third-party carriers. Our own pickup and delivery service gets us to you faster.",
    days: {
      modo: { label: "Monday & Thursday", short: "Mon · Thu" },
      mi:   { label: "Wednesday",         short: "Wed" },
      difr: { label: "Tuesday & Friday",  short: "Tue · Fri" },
    },
    spedition_title: "Shipping & fleet",
    spedition_hint: "Our shipping team is happy to help with route details anytime.",
    stops_title: "Regions covered",
    cta_contact: "Request a route",
  },
  fr: {
    badge: "FLOTTE INTERNE",
    overline: "— Tournées & horaires",
    title_1: "Nous venons",
    title_2: "chez vous.",
    intro:
      "Un facteur clé de notre flexibilité est notre indépendance des transporteurs externes. Notre service d'enlèvement et de livraison interne est plus rapide chez vous.",
    days: {
      modo: { label: "Lundi & jeudi",  short: "Lun · Jeu" },
      mi:   { label: "Mercredi",       short: "Mer" },
      difr: { label: "Mardi & vendredi", short: "Mar · Ven" },
    },
    spedition_title: "Expédition & flotte",
    spedition_hint: "Notre équipe d'expédition vous conseille volontiers.",
    stops_title: "Régions desservies",
    cta_contact: "Demander une tournée",
  },
  it: {
    badge: "FLOTTA INTERNA",
    overline: "— Tratte & orari",
    title_1: "Veniamo",
    title_2: "da Voi.",
    intro:
      "Un fattore chiave della nostra flessibilità è l'indipendenza dai trasportatori esterni. Il nostro servizio interno di ritiro e consegna ci permette di essere ancora più rapidi.",
    days: {
      modo: { label: "Lunedì & giovedì",  short: "Lun · Gio" },
      mi:   { label: "Mercoledì",         short: "Mer" },
      difr: { label: "Martedì & venerdì", short: "Mar · Ven" },
    },
    spedition_title: "Spedizione & flotta",
    spedition_hint: "Il nostro team spedizione è a disposizione per ogni dettaglio.",
    stops_title: "Regioni servite",
    cta_contact: "Richiedi una tratta",
  },
};

// Approximate positions of each region within our 1000x560 SVG canvas.
// Not geographically exact — visually laid out to read cleanly.
const CITIES = {
  Basel:              { x: 285, y: 105, label: "Basel" },
  Jura:               { x: 215, y: 165, label: "Jura" },
  Solothurn:          { x: 335, y: 175, label: "Solothurn" },
  Aargau:             { x: 415, y: 155, label: "Aargau" },
  Bern:               { x: 320, y: 275, label: "Bern" },
  Luzern:             { x: 465, y: 240, label: "Luzern" },
  Obwalden:           { x: 450, y: 305, label: "OW" },
  Nidwalden:          { x: 490, y: 290, label: "NW" },
  "Zürich":           { x: 545, y: 165, label: "Zürich" },
  Winterthur:         { x: 590, y: 130, label: "Winterthur" },
  "Zürcher Oberland": { x: 620, y: 195, label: "Z. Oberland" },
  Thurgau:            { x: 680, y: 125, label: "Thurgau" },
  "St. Gallen":       { x: 745, y: 195, label: "St. Gallen" },
  Rheintal:           { x: 810, y: 235, label: "Rheintal" },
  Mels:               { x: 750, y: 270, label: "Mels · HQ" },
};

const ROUTES = {
  modo: {
    color: "#f59e0b", // amber
    stops: ["Mels", "Luzern", "Obwalden", "Nidwalden", "Aargau", "Zürich", "Mels"],
    region_stops: ["Luzern", "Obwalden", "Nidwalden", "Aargau", "Zürich"],
    display: "Luzern — OW — NW — Aargau — Zürich",
  },
  mi: {
    color: "#a78bfa", // violet
    stops: ["Mels", "Aargau", "Solothurn", "Bern", "Basel", "Jura", "Mels"],
    region_stops: ["Aargau", "Solothurn", "Bern", "Basel", "Jura"],
    display: "Aargau — Solothurn — Bern — Basel — Jura",
  },
  difr: {
    color: "#34d399", // emerald
    stops: ["Mels", "Rheintal", "St. Gallen", "Thurgau", "Winterthur", "Zürcher Oberland", "Mels"],
    region_stops: ["Rheintal", "St. Gallen", "Thurgau", "Winterthur", "Zürcher Oberland"],
    display: "Rheintal — St. Gallen — Thurgau — Winterthur — Z. Oberland",
  },
};

const DAY_KEYS = ["modo", "mi", "difr"];

// Build an SVG polyline string from a route's ordered stops.
function polylinePoints(stops) {
  return stops.map((k) => `${CITIES[k].x},${CITIES[k].y}`).join(" ");
}

export const Routen = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [activeDay, setActiveDay] = useState("difr"); // default: eastern route (Mels area)

  const activeRoute = ROUTES[activeDay];
  const activeCitySet = useMemo(() => new Set(activeRoute.stops), [activeRoute]);

  const goToContact = () => {
    try {
      const routeName = c.days[activeDay].label;
      window.dispatchEvent(new CustomEvent("fk:prefill-contact", {
        detail: {
          message:
            `Anfrage zur Route ${routeName}:\n` +
            `• Stationen: ${activeRoute.display}\n\n` +
            `Bitte um Rückmeldung, an welchem Tag Sie in unserer Nähe vorbeikommen — wir haben Ware zur Abholung / Lieferung.`,
        },
      }));
    } catch (_) { /* ignore */ }
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="routen"
      data-testid="routen-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 right-1/4 w-[500px] h-[500px] rounded-full bg-white/[0.03] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <Truck className="w-3 h-3" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        {/* Main card */}
        <div className="reveal bg-[#121214] border border-white/10 grid grid-cols-1 lg:grid-cols-12">
          {/* LEFT: interactive map */}
          <div className="lg:col-span-8 relative p-6 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-gradient-to-br from-[#0E0E10] to-[#141416]">
            {/* Day tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {DAY_KEYS.map((k) => {
                const active = activeDay === k;
                const color = ROUTES[k].color;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveDay(k)}
                    data-testid={`route-tab-${k}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 text-[12px] uppercase tracking-[0.14em] border transition-colors ${active ? "bg-white text-black border-white" : "text-zinc-300 border-white/15 hover:border-white/40 hover:text-white"}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{ backgroundColor: color }}
                    />
                    {c.days[k].label}
                  </button>
                );
              })}
            </div>

            {/* SVG map */}
            <svg
              viewBox="0 0 1000 400"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto"
              aria-label="Schweizer Routenkarte"
              data-testid="route-map"
            >
              <defs>
                <pattern id="fk-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                </pattern>
                <filter id="fk-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <marker id="fk-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={activeRoute.color} />
                </marker>
              </defs>

              {/* background */}
              <rect x="0" y="0" width="1000" height="400" fill="url(#fk-grid)" />

              {/* faint outline hint of Switzerland (very stylised) */}
              <path
                d="M 150 190 C 180 130, 260 90, 380 85 C 500 80, 620 85, 730 105 C 820 125, 870 165, 870 220 C 870 275, 800 305, 720 310 C 620 320, 520 315, 430 310 C 350 305, 260 290, 200 260 C 160 240, 145 215, 150 190 Z"
                fill="rgba(255,255,255,0.02)"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />

              {/* Inactive routes as dim dashed lines */}
              {DAY_KEYS.filter((k) => k !== activeDay).map((k) => (
                <polyline
                  key={k}
                  points={polylinePoints(ROUTES[k].stops)}
                  fill="none"
                  stroke={ROUTES[k].color}
                  strokeOpacity="0.18"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                />
              ))}

              {/* Active route */}
              <polyline
                points={polylinePoints(activeRoute.stops)}
                fill="none"
                stroke={activeRoute.color}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#fk-glow)"
                data-testid="active-route-line"
              />

              {/* City dots */}
              {Object.entries(CITIES).map(([k, city]) => {
                const onActive = activeCitySet.has(k);
                const isHq = k === "Mels";
                const r = isHq ? 8 : (onActive ? 6 : 4);
                const fill = isHq ? "#ffffff" : (onActive ? activeRoute.color : "rgba(255,255,255,0.35)");
                return (
                  <g key={k} data-testid={`city-${k}`}>
                    {onActive && (
                      <circle cx={city.x} cy={city.y} r={r + 6} fill={activeRoute.color} fillOpacity="0.14" />
                    )}
                    <circle cx={city.x} cy={city.y} r={r} fill={fill} />
                    {isHq && <circle cx={city.x} cy={city.y} r={r + 4} fill="none" stroke="#fff" strokeOpacity="0.6" />}
                    <text
                      x={city.x + 12}
                      y={city.y + 4}
                      className={`select-none ${onActive || isHq ? "fill-white" : "fill-zinc-500"}`}
                      style={{ fontSize: isHq ? 13 : 11, fontWeight: isHq ? 600 : 400 }}
                    >
                      {city.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* legend below map */}
            <div className="mt-4 flex items-center gap-6 text-[11px] text-zinc-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-white inline-block" />
                Mels · Hauptsitz
              </span>
              {DAY_KEYS.map((k) => (
                <span key={k} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: ROUTES[k].color }} />
                  {c.days[k].short}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT: schedule + contact */}
          <div className="lg:col-span-4 p-6 md:p-10 flex flex-col">
            <div className="overline mb-4 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" />
              {c.stops_title}
            </div>

            <div className="space-y-4 mb-8">
              {DAY_KEYS.map((k) => {
                const active = activeDay === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveDay(k)}
                    data-testid={`route-panel-${k}`}
                    className={`w-full text-left border transition-colors ${active ? "border-white bg-white/[0.03]" : "border-white/10 hover:border-white/25"}`}
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="w-1.5 h-1.5 rounded-full inline-block"
                          style={{ backgroundColor: ROUTES[k].color }}
                        />
                        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                          {c.days[k].label}
                        </div>
                      </div>
                      <div className={`text-sm leading-relaxed ${active ? "text-white" : "text-zinc-300"}`}>
                        {ROUTES[k].region_stops.join("  ·  ")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Spedition card */}
            <div className="border border-white/10 p-5 bg-[#0E0E10] mt-auto">
              <div className="overline mb-3 flex items-center gap-2">
                <Truck className="w-3.5 h-3.5" />
                {c.spedition_title}
              </div>
              <p className="text-[13px] text-zinc-400 leading-relaxed mb-4">
                {c.spedition_hint}
              </p>
              <div className="space-y-2 mb-5">
                <a
                  href="mailto:spedition@f-kamm.ch"
                  data-testid="spedition-mail"
                  className="flex items-center gap-2 text-sm text-white hover:text-zinc-300 transition-colors"
                >
                  <Mail className="w-4 h-4 text-zinc-400" />
                  spedition@f-kamm.ch
                </a>
                <a
                  href="tel:+41817232417"
                  data-testid="spedition-phone"
                  className="flex items-center gap-2 text-sm text-white hover:text-zinc-300 transition-colors"
                >
                  <Phone className="w-4 h-4 text-zinc-400" />
                  081 723 24 17
                </a>
              </div>
              <button
                type="button"
                onClick={goToContact}
                data-testid="route-cta"
                className="w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white text-[12px] uppercase tracking-[0.16em] px-4 py-3 hover:bg-white hover:text-black transition-colors"
              >
                {c.cta_contact}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Routen;
